<script lang="ts" setup>
import { computed, ref } from "vue";
import { useAllDocumentSpaceConfigsQuery } from "@/graphql/operations";
import { Formatter } from "@/util";
import CreateTemplateModal from "./CreateTemplateModal.vue";
import DeleteTemplateModal from "./DeleteTemplateModal.vue";
import type { TemplateListItem } from "./templateTypes";

const { data, fetching, executeQuery } = useAllDocumentSpaceConfigsQuery();
const configs = computed<TemplateListItem[]>(() => {
  const teams = data.value?.myAdministratedTeams?.nodes;
  if (teams) {
    return teams.filter(Boolean).flatMap((team) =>
      (team!.documentSpaceConfigs?.nodes ?? [])
        .filter(Boolean)
        .map((config) => ({
          id: config!.id,
          groupId: config!.groupId,
          name: config!.name ?? "",
          teamId: team!.id,
          teamName: config!.team?.name ?? team!.name,
          groupName: config!.tenantGroup?.name ?? config!.groupId,
          updatedAt: config!.updatedAt,
          spaceCount: config!.spaceCount ?? 0,
        })),
    );
  }
  return [];
});
const hasAdminTeams = computed(
  () =>
    (data.value?.myAdministratedTeams?.nodes?.filter(Boolean).length ?? 0) > 0,
);
const showCreateTemplateModal = ref(false);
const selectedTemplateForDeletion = ref<TemplateListItem | null>(null);

const refreshTemplates = async () => {
  selectedTemplateForDeletion.value = null;
  await executeQuery({ requestPolicy: "network-only" });
};
</script>

<template>
  <div v-if="fetching && configs.length === 0" class="loading_status">
    <LoadingSpinner color="var(--classic-text, black)" />
  </div>

  <template v-else-if="configs && configs.length > 0">
    <div class="title_container">
      <h4>{{ $t("templates.title") }}</h4>
      <div class="controls">
        <div class="control">
          <button
            v-if="hasAdminTeams"
            class="button primary"
            @click="showCreateTemplateModal = true"
          >
            {{ $t("templates.actions.new") }}
          </button>
        </div>
      </div>
    </div>
    <div>
      <table class="data_table clickable">
        <thead>
          <th>{{ $t("templates.columns.name") }}</th>
          <th>{{ $t("templates.columns.team") }}</th>
          <th>{{ $t("templates.columns.spaces") }}</th>
          <th>{{ $t("templates.columns.lastModified") }}</th>
          <th class="actions_header">
            {{ $t("templates.columns.actions") }}
          </th>
        </thead>
        <tbody>
          <tr v-for="config in configs" :key="config.id">
            <td>
              <router-link
                :to="{
                  name: 'TemplateEditor',
                  params: { id: config.id },
                }"
              >
                {{ config.name || $t("templates.unnamed") }}
              </router-link>
            </td>
            <td>
              <router-link
                :to="{
                  name: 'TemplateEditor',
                  params: { id: config.id },
                }"
              >
                {{ config.teamName }}
              </router-link>
            </td>
            <td>
              <router-link
                :to="{
                  name: 'TemplateEditor',
                  params: { id: config.id },
                }"
              >
                {{ config.spaceCount }}
              </router-link>
            </td>
            <td>
              <router-link
                :to="{
                  name: 'TemplateEditor',
                  params: { id: config.id },
                }"
              >
                {{
                  Formatter.formatDate(
                    new Date(config.updatedAt),
                    "%Y-%m-%d %H:%M",
                  )
                }}
              </router-link>
            </td>
            <td class="actions_cell">
              <button
                class="button dangerous-outline"
                :title="$t('templates.actions.delete')"
                @click.stop="selectedTemplateForDeletion = config"
              >
                <span class="material-symbols-outlined">delete</span>
                {{ $t("templates.actions.delete") }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>

  <div v-else class="empty_pane">
    <div>
      <h2>{{ $t("templates.emptyState.title") }}</h2>
      <p>{{ $t("templates.emptyState.info") }}</p>
      <button
        v-if="hasAdminTeams"
        class="button primary"
        @click="showCreateTemplateModal = true"
      >
        {{ $t("templates.actions.new") }}
      </button>
    </div>
  </div>

  <CreateTemplateModal
    v-if="showCreateTemplateModal"
    @close="showCreateTemplateModal = false"
  />

  <DeleteTemplateModal
    v-if="selectedTemplateForDeletion"
    :template="selectedTemplateForDeletion"
    :templates="configs"
    @close="selectedTemplateForDeletion = null"
    @deleted="refreshTemplates"
  />
</template>

<style scoped>
.title_container {
  display: flex;
  align-items: center;
  margin-bottom: 0.8em;
}

.title_container h4 {
  font-weight: 500;
  flex: 1;
  margin: 0;
}

button {
  font-size: 0.9em;
}

.pane {
  height: 100%;
}

.page_content {
  height: 100%;
}

.empty_pane {
  height: calc(100% - 75px);
  display: flex;
  justify-content: center;
}

.empty_pane > div {
  flex: none;
  text-align: center;
  margin-top: 5em;
}

.empty_pane h2 {
  margin-bottom: 0;
}

.empty_pane p {
  margin: 0.6em 0 1.5em 0;
}

table.data_table {
}

table a {
  text-decoration: none;
  width: 100%;
  color: inherit;
  font-weight: normal;
  display: inline-block;
}

table th {
  background: var(--classic-surface-raised, #f2f2f2);
}

.actions_header,
.actions_cell {
  text-align: right;
  white-space: nowrap;
}

.actions_cell {
  vertical-align: middle;
}

.actions_cell .button {
  font-size: 0.8em;
  line-height: 1.4;
  padding: 0.35em 0.65em;
}
</style>
